import { FieldPath, FieldValues, useController } from "react-hook-form";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Autocomplete, AutocompleteRenderInputParams, TextField } from "@mui/material";
import { RhfInputProps } from "../../../../../Forms/RhfInputProps";
import { SelectionListItem } from "../../../../../Forms/SelectionListItem";
import { MuiSelectChip } from "../../../../../Layout/Select/MuiSelectChip";
import { textFieldDefaultProps } from "../../../../../Forms/TextFieldDefaultProps";

type TtProtectionMultiSelectProps = {
  apiUrl: string;
  errorMessage: string;
  label: ReactNode;
};

export const TtProtectionMultiSelect = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  apiUrl,
  errorMessage,
}: RhfInputProps<TFieldValues, TName> & TtProtectionMultiSelectProps) => {
  const [menuItems, setMenuItems] = useState<SelectionListItem<string | number>[]>([]);
  const { field } = useController({
    control,
    name,
  });

  const fetchCollection = useCallback(() => {
    axios
      .get<SelectionListItem<string | number>[]>(apiUrl)
      .then((response) => setMenuItems(response.data))
      .catch(() => toast.error(errorMessage));
  }, [apiUrl, errorMessage]);

  useEffect(() => {
    fetchCollection();
  }, [fetchCollection]);

  const labels = Object.fromEntries(menuItems.map((option) => [option.Id, option.Label]));

  return (
    <Autocomplete
      {...field}
      multiple
      options={menuItems ?? []}
      value={menuItems.filter((menuItem) => field.value.includes(menuItem.Id))}
      getOptionLabel={(option) => option.Label}
      onChange={(_, selectedValues) => {
        field.onChange(selectedValues.map((item) => item.Id));
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          {...textFieldDefaultProps}
          label={label}
          InputProps={{ ...params.InputProps, type: "search" }}
        />
      )}
      renderTags={(values, getTagProps) =>
        values.map((value, index) => (
          <MuiSelectChip
            getTagProps={getTagProps}
            labels={labels}
            index={index}
            valueId={value.Id.toString()}
          />
        ))
      }
      size="small"
      filterSelectedOptions
      disableCloseOnSelect
    />
  );
};
